import { useEffect, useRef, useState } from "react";
import {
  RadixDialog,
  RadixDialogContent,
  RadixDialogHeader,
  RadixDialogTitle,
  RadixDialogDescription,
} from "../../vendor/ds-react";
import {
  useFullState,
  useCurrentStateAsRef,
  useWriteContext,
} from "../../vendor/src/features/remotion-editor/editor/utils/use-context";
import type { ImageAsset } from "../../vendor/src/features/remotion-editor/editor/assets/assets";
import { connectImageActions } from "../host/image-actions";
import { requestHost } from "../host/client";
import { measureMedia } from "./measure-media";
import { replaceImage } from "./image-replacement";
import { editorUserMessage } from "../../protocol/user-message";
import {ImageMaskEditor} from "./ImageMaskEditor";
import { readImageEditSource,readImageEditExtras,type ImageEditReference } from "../../protocol/image-edit";

type Props = {
  disabled: boolean;
  save: () => Promise<boolean>;
  revision: () => string | undefined;
};
export function ImageTools(props: Props) {
  const state = useFullState();
  const [clipId, setClipId] = useState<string | null>(null);
  useEffect(() => connectImageActions(setClipId), []);
  const selected = state.selectedItems.filter(
    (id) => state.undoableState.items[id]?.type === "image",
  );
  return (
    <>
      <button
        type="button"
        disabled={props.disabled || selected.length !== 1 || !!state.undoableState.items[selected[0]]?.sourceBrandIntro}
        title="타임라인의 이미지 클립 하나를 선택해 주세요."
        onClick={() => setClipId(selected[0]!)}
      >
        이미지 수정·교체
      </button>
      {clipId && (state.undoableState.items[clipId]?.sourceBrandIntro?<div><p role="status">오프닝 카드는 속성의 오프닝 카드 문구에서 수정해 주세요.</p><button type="button" onClick={()=>setClipId(null)}>닫기</button></div>:(
        <ImageSession
          key={clipId}
          clipId={clipId}
          {...props}
          onClose={() => setClipId(null)}
        />
      ))}
    </>
  );
}
function ImageSession({
  clipId,
  save,
  revision,
  onClose,
}: Props & { clipId: string; onClose: () => void }) {
  const current = useCurrentStateAsRef(),
    { setState } = useWriteContext();
  const [original] = useState(() => current.current.undoableState);
  const source = original.items[clipId];
  const sourceAsset =
    source?.type === "image" ? original.assets[source.assetId] : null;
  const choices = Object.values(original.assets).filter(
    (a): a is ImageAsset =>
      a.type === "image" && !!a.remoteUrl && a.id !== sourceAsset?.id,
  );
  const [candidate, setCandidate] = useState<ImageAsset | null>(null),
    [scope, setScope] = useState<"clip" | "source">("clip");
  const [instruction, setInstruction] = useState(""),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const [maskEnabled,setMaskEnabled]=useState(false),[mask,setMask]=useState<string|undefined>();
  const [references,setReferences]=useState<ImageEditReference[]>([]);
  const referenceChoices=choices.filter(a=>a.remoteUrl?.startsWith('https://'));
  const changeMask=(value:string|undefined)=>{setMask(value);setConfirmed(false);setCandidate(null);};
  const active = useRef(true),
    running = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  async function generate() {
    if (running.current || !confirmed || !instruction.trim() || (maskEnabled&&!mask)) return;
    running.current = true;
    setBusy(true);
    setMessage("");
    try {
      if (current.current.undoableState !== original)
        throw Error("편집 내용이 바뀌었습니다. 창을 닫고 다시 열어 주세요.");
      readImageEditSource(original, clipId);
      const extras={...(maskEnabled?{maskBase64:mask}:{}),...(references.length?{references}:{})};
      readImageEditExtras(original,clipId,extras);
      if (!(await save())) throw Error("현재 편집본을 먼저 저장해 주세요.");
      if (!active.current) return;
      const saved = revision();
      if (!saved) throw Error("저장 시점을 확인하지 못했습니다.");
      if (current.current.undoableState !== original)
        throw Error("편집 내용이 바뀌어 생성을 시작하지 않았습니다.");
      const response = (await requestHost({
        method: "POST",
        path: "/__waka/image-edit",
        body: { clipId, revision: saved, instruction, confirmed: true, ...extras },
      })) as { url?: unknown };
      if (!active.current) return;
      if (
        typeof response.url !== "string" ||
        !response.url.startsWith("https://")
      )
        throw Error("수정 이미지 주소를 확인하지 못했습니다.");
      const dimensions = await measureMedia("image", response.url);
      if (!active.current) return;
      setCandidate({
        id: crypto.randomUUID(),
        type: "image",
        filename: "AI 수정 이미지.png",
        size: 0,
        remoteUrl: response.url,
        remoteFileKey: null,
        mimeType: "image/png",
        ...dimensions,
      });
      setMessage(
        "수정안을 확인한 뒤 적용해 주세요. 원본은 아직 바꾸지 않았습니다.",
      );
    } catch (error) {
      if (active.current)
        setMessage(
          editorUserMessage(
            error,
            "이미지 수정안을 만들지 못했습니다. 다시 시도해 주세요.",
          ),
        );
    } finally {
      running.current = false;
      if (active.current) {
        setBusy(false);
        setConfirmed(false);
      }
    }
  }
  function apply() {
    try {
      if (!candidate) return;
      if (current.current.undoableState !== original)
        throw Error(
          "편집 내용이 바뀌어 적용하지 않았습니다. 창을 닫고 다시 열어 주세요.",
        );
      const after = replaceImage(current.current, clipId, candidate, scope);
      setState({
        commitToUndoStack: true,
        update: (prev) =>
          prev.undoableState === original
            ? {
                ...prev,
                undoableState: after.undoableState,
                assetStatus: after.assetStatus,
              }
            : prev,
      });
      onClose();
    } catch (error) {
      setMessage(editorUserMessage(error));
    }
  }
  return (
    <RadixDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <RadixDialogContent
        className="gap-4"
        style={{
          position: "fixed",
          inset: 0,
          margin: "auto",
          width: 720,
          maxWidth: "calc(100vw - 32px)",
          height: "fit-content",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          transform: "none",
          translate: "none",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          padding: 24,
          borderRadius: 16,
          background: "#fff",
          color: "#243443",
          fontFamily: "Precision UI, Pretendard, sans-serif",
          boxShadow: "0 20px 80px #102b3940",
        }}
      >
        <RadixDialogHeader>
          <RadixDialogTitle>이미지 수정·교체</RadixDialogTitle>
          <RadixDialogDescription>
            위치·길이·크롭은 유지합니다. 적용 전까지 원본은 바뀌지 않으며 적용
            후에는 되돌릴 수 있습니다.
          </RadixDialogDescription>
        </RadixDialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <figure>
            <figcaption>원본</figcaption>
            {sourceAsset?.remoteUrl && (
              <img
                src={sourceAsset.remoteUrl}
                alt="원본 이미지"
                className="h-44 w-full rounded object-contain"
                style={{
                  width: "100%",
                  height: 180,
                  objectFit: "contain",
                  borderRadius: 8,
                  background: "#f5f7fa",
                }}
              />
            )}
          </figure>
          <figure>
            <figcaption>교체 미리보기</figcaption>
            {candidate?.remoteUrl ? (
              <img
                src={candidate.remoteUrl}
                alt="교체 이미지 미리보기"
                className="h-44 w-full rounded object-contain"
                style={{
                  width: "100%",
                  height: 180,
                  objectFit: "contain",
                  borderRadius: 8,
                  background: "#f5f7fa",
                }}
              />
            ) : (
              <p>이미지를 선택하거나 AI 수정안을 만드세요.</p>
            )}
          </figure>
        </div>
        <label>
          현재 작업의 이미지로 교체
          <select
            aria-label="교체 이미지 선택"
            disabled={busy}
            value={
              candidate && choices.some((a) => a.id === candidate.id)
                ? candidate.id
                : ""
            }
            onChange={(event) =>
              setCandidate(
                choices.find((a) => a.id === event.target.value) ?? null,
              )
            }
            className="block w-full rounded border p-2"
          >
            <option value="">이미지 선택</option>
            {choices.map((a) => (
              <option key={a.id} value={a.id}>
                {a.filename}
              </option>
            ))}
          </select>
          <small>
            추가할 이미지는 상단 자료함이나 파일 추가에서 먼저 가져올 수
            있습니다.
          </small>
        </label>
        <details>
          <summary>AI로 이미지 수정</summary>
          <label>
            수정할 내용
            <textarea
              aria-label="이미지 수정 지시"
              maxLength={2000}
              disabled={busy}
              value={instruction}
              onChange={(event) => {setInstruction(event.target.value);setConfirmed(false);setCandidate(null);}}
              className="block w-full rounded border p-2"
              placeholder="예: 배경만 밝은 회색으로 바꿔 주세요."
            />
          </label>
          <button
            type="button"
            disabled={busy}
            className="mb-2 rounded border px-3 py-1 text-sm"
            onClick={() => {setInstruction("이미지 안의 글자만 지워 주세요. 다른 요소는 그대로 유지해 주세요.");setConfirmed(false);setCandidate(null);}}
          >
            글자 제거 지시 넣기
          </button>
          <label className="flex gap-2"><input type="checkbox" checked={maskEnabled} disabled={busy} onChange={e=>{setMaskEnabled(e.target.checked);changeMask(undefined);}}/>수정 영역 직접 지정</label>
          {maskEnabled&&sourceAsset?.remoteUrl&&<ImageMaskEditor key={sourceAsset.remoteUrl} url={sourceAsset.remoteUrl} disabled={busy} onChange={changeMask}/>}
          {maskEnabled&&!mask&&<p>수정할 영역을 칠해 주세요. 전체 이미지를 수정하려면 영역 지정을 해제하세요.</p>}
          <fieldset disabled={busy}><legend>참조 이미지 (최대 2장)</legend>
           <p>현재 작업의 다른 이미지를 선택합니다. 추가할 이미지는 자료함에 먼저 가져와 주세요.</p>
           {[0,1].map(index=>{const ref=references[index];return <div key={index} className="flex gap-2 my-2">
            <select aria-label={`참조 이미지 ${index+1}`} value={ref?.assetId??''} disabled={index===1&&!references[0]} onChange={e=>{const next=[...references];if(!e.target.value)next.splice(index,1);else next[index]={assetId:e.target.value,kind:ref?.kind??'object'};setReferences(next);setConfirmed(false);setCandidate(null);}}>
             <option value="">참조 없음</option>{referenceChoices.filter(a=>!references.some((r,i)=>i!==index&&r.assetId===a.id)).map(a=><option key={a.id} value={a.id}>{a.filename}</option>)}
            </select>
            {ref&&<><img src={referenceChoices.find(a=>a.id===ref.assetId)?.remoteUrl??undefined} alt={`참조 ${index+1}`} style={{width:48,height:48,objectFit:'contain'}}/><select aria-label={`참조 ${index+1} 용도`} value={ref.kind} onChange={e=>{setReferences(references.map((r,i)=>i===index?{...r,kind:e.target.value==='identity'?'identity':'object'}:r));setConfirmed(false);setCandidate(null);}}><option value="object">사물·로고·디자인</option><option value="identity">인물·캐릭터</option></select></>}
           </div>;})}
          </fieldset>
          <label className="flex gap-2">
            <input
              type="checkbox"
              checked={confirmed}
              disabled={busy}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            이미지 생성 크레딧을 사용해 수정안을 만듭니다.
          </label>
          <button
            type="button"
            className="mt-2 rounded-lg border px-3 py-2 text-sm disabled:opacity-40"
            disabled={busy || !confirmed || !instruction.trim() || (maskEnabled&&!mask)}
            onClick={() => void generate()}
          >
            {busy ? "수정안 만드는 중…" : "AI 수정안 만들기"}
          </button>
        </details>
        <label>
          적용 범위
          <select
            aria-label="이미지 적용 범위"
            disabled={busy}
            value={scope}
            onChange={(event) =>
              setScope(event.target.value === "source" ? "source" : "clip")
            }
            className="ml-2 rounded border p-2"
          >
            <option value="clip">선택한 클립만</option>
            <option value="source">
              같은 원본의 클립 모두 (
              {
                Object.values(original.items).filter(
                  (item) =>
                    item.type === "image" && item.assetId === sourceAsset?.id,
                ).length
              }
              개)
            </option>
          </select>
        </label>
        {message && <p role="status">{message}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg border px-3 py-2 text-sm"
            onClick={onClose}
          >
            취소
          </button>
          <button
            type="button"
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            disabled={busy || !candidate}
            onClick={apply}
          >
            이미지 적용
          </button>
        </div>
      </RadixDialogContent>
    </RadixDialog>
  );
}
